import React from 'react';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getErrorMessage, getNetworkErrorMessage, isNetworkError } from '@/lib/errors';
import { BUDGET_RETRY } from '@/lib/translations';

export interface ReadingErrorProps {
  title: string;
  error: unknown;
  onRetry: () => void;
  /** h3 inside a Painel section (under its h2); h2 where the strip stands directly under the page title. */
  headingLevel?: 'h2' | 'h3';
}

// One section failing never blanks the page: the header, the period and the other section stay usable, so this
// fills only its own panel. It is an inline strip, left-aligned between hairlines — not the centred empty state a
// first run or a quiet month gets — so "something broke" never reads like "nothing here yet". The retry is quiet
// because Painel's amber is already spent on "Nova Transação".
export const ReadingError: React.FC<ReadingErrorProps> = ({ title, error, onRetry, headingLevel: Heading = 'h3' }) => {
  const info = isNetworkError(error) ? getNetworkErrorMessage() : getErrorMessage(error);

  return (
    <div className="flex flex-col gap-4 border-y border-border/60 py-5 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-foreground" aria-hidden="true" />
        <div className="min-w-0 space-y-0.5 text-sm">
          <Heading className="font-semibold text-foreground">{title}</Heading>
          <p className="text-muted-foreground">{info.description}</p>
        </div>
      </div>
      <Button variant="secondary" size="sm" onClick={onRetry} className="ml-7 self-start sm:ml-0 sm:self-auto">
        <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
        {BUDGET_RETRY}
      </Button>
    </div>
  );
};
